import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_KEY, LIVE_API_BASE_URL, getApiHeaders } from '../config/api';

const liveApiBase = `${LIVE_API_BASE_URL.replace(/\/$/, '')}/v1/live`;

type PresignResponse = {
  filterId: string;
  uploadUrl: string;
  fileKey: string;
  fileUrl: string;
  thumbnailUploadUrl?: string;
  thumbnailUrl?: string;
};

const CreateLiveFilter: React.FC = () => {
  const navigate = useNavigate();
  const filterFileRef = useRef<HTMLInputElement>(null);
  const thumbnailFileRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [filterFile, setFilterFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [presignResult, setPresignResult] = useState<PresignResponse | null>(null);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const handleThumbnailChange = (file: File | null) => {
    setThumbnailFile(file);
    if (thumbnailPreview) URL.revokeObjectURL(thumbnailPreview);
    if (file?.type.startsWith('image/')) {
      setThumbnailPreview(URL.createObjectURL(file));
    } else {
      setThumbnailPreview('');
    }
  };

  const handleUpload = async () => {
    if (!name.trim()) {
      toast.error('Filter name is required.');
      return;
    }
    if (!filterFile) {
      toast.error('Select a .deepar filter file.');
      return;
    }

    const headers = headerWithAuth();
    if (!headers) return;

    setUploading(true);
    try {
      const presignRes = await fetch(`${liveApiBase}/filters/presign`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: name.trim(),
          ...(thumbnailFile ? { thumbnailContentType: thumbnailFile.type || 'image/jpeg' } : {}),
        }),
      });
      const presignJson = await presignRes.json();
      if (!presignRes.ok) throw new Error(presignJson.message || 'Failed to get upload URL');
      const presign = presignJson.data as PresignResponse;

      const filterBuffer = await filterFile.arrayBuffer();
      const filterUploadRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        body: filterBuffer,
        headers: { 'Content-Type': 'application/octet-stream' },
      });
      if (!filterUploadRes.ok) throw new Error('Failed to upload .deepar file');

      if (thumbnailFile && presign.thumbnailUploadUrl) {
        const thumbBuffer = await thumbnailFile.arrayBuffer();
        const thumbUploadRes = await fetch(presign.thumbnailUploadUrl, {
          method: 'PUT',
          body: thumbBuffer,
          headers: { 'Content-Type': thumbnailFile.type || 'image/jpeg' },
        });
        if (!thumbUploadRes.ok) throw new Error('Failed to upload thumbnail');
      }

      setPresignResult(presign);
      toast.success('Files uploaded — save the filter to complete.');
    } catch (error) {
      toast.error((error as Error).message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!presignResult) {
      toast.error('Upload files first.');
      return;
    }

    const headers = headerWithAuth();
    if (!headers) return;

    setSaving(true);
    try {
      const res = await fetch(`${liveApiBase}/filters`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          filterId: presignResult.filterId,
          name: name.trim(),
          fileKey: presignResult.fileKey,
          ...(presignResult.thumbnailUrl ? { thumbnailUrl: presignResult.thumbnailUrl } : {}),
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to save filter');
      toast.success(json.message || 'Filter saved');
      navigate('/live-filters');
    } catch (error) {
      toast.error((error as Error).message || 'Failed to save filter');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Upload Live Filter</h1>
            <p className='text-sm text-gray-400'>Upload a .deepar file and save it to the filter catalog.</p>
          </div>
          <button
            onClick={() => navigate('/live-filters')}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
          >
            Back to Filters
          </button>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {/* Step 1 — Upload */}
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-4'>
            <div>
              <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 1</p>
              <h2 className='text-lg font-semibold text-white'>Upload Files</h2>
              <p className='text-xs text-gray-400 mt-1'>Upload the .deepar file and an optional thumbnail.</p>
            </div>

            <div>
              <label className='block text-xs text-gray-400 mb-1'>Filter Name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FF9C15]/60'
                placeholder='e.g. Viking Helmet'
              />
            </div>

            <div>
              <label className='block text-xs text-gray-400 mb-1'>DeepAR Filter File (.deepar) *</label>
              <input
                ref={filterFileRef}
                type='file'
                accept='.deepar,application/octet-stream'
                onChange={(e) => setFilterFile(e.target.files?.[0] || null)}
                className='w-full text-sm text-gray-300'
              />
              {filterFile && (
                <div className='mt-1 text-xs text-gray-400'>{filterFile.name} ({(filterFile.size / 1024).toFixed(1)} KB)</div>
              )}
            </div>

            <div>
              <label className='block text-xs text-gray-400 mb-1'>Thumbnail Image (optional)</label>
              <input
                ref={thumbnailFileRef}
                type='file'
                accept='image/*'
                onChange={(e) => handleThumbnailChange(e.target.files?.[0] || null)}
                className='w-full text-sm text-gray-300'
              />
              {thumbnailPreview && (
                <img
                  src={thumbnailPreview}
                  alt='Thumbnail preview'
                  className='mt-2 h-24 w-24 object-cover rounded-lg border border-white/10'
                />
              )}
            </div>

            <button
              onClick={handleUpload}
              disabled={uploading || !!presignResult}
              className='w-full py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60'
            >
              {uploading ? 'Uploading...' : presignResult ? 'Uploaded' : 'Upload Files'}
            </button>

            {presignResult && (
              <div className='bg-white/5 border border-white/10 rounded-lg p-3 text-xs text-gray-300 space-y-1'>
                <div className='text-emerald-300 font-semibold'>Files uploaded successfully</div>
                <div className='truncate'>Key: {presignResult.fileKey}</div>
                <a
                  href={presignResult.fileUrl}
                  target='_blank'
                  rel='noreferrer'
                  className='text-[#FF9C15] hover:underline break-all block'
                >
                  {presignResult.fileUrl}
                </a>
                {presignResult.thumbnailUrl && (
                  <a
                    href={presignResult.thumbnailUrl}
                    target='_blank'
                    rel='noreferrer'
                    className='text-sky-300 hover:underline break-all block'
                  >
                    Thumbnail: {presignResult.thumbnailUrl}
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Step 2 — Save */}
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-4'>
            <div>
              <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 2</p>
              <h2 className='text-lg font-semibold text-white'>Save Filter</h2>
              <p className='text-xs text-gray-400 mt-1'>Persist the filter record in the database.</p>
            </div>

            <div className='space-y-3 text-sm'>
              <div className='bg-black/20 border border-white/10 rounded-lg p-4 space-y-2 text-xs text-gray-400'>
                <div className='flex justify-between'>
                  <span>Name</span>
                  <span className='text-white'>{name || '—'}</span>
                </div>
                <div className='flex justify-between'>
                  <span>Filter ID</span>
                  <span className='text-white font-mono'>{presignResult?.filterId || '—'}</span>
                </div>
                <div className='flex justify-between'>
                  <span>File</span>
                  <span className='text-white'>{filterFile?.name || '—'}</span>
                </div>
                <div className='flex justify-between'>
                  <span>Thumbnail</span>
                  <span className='text-white'>{thumbnailFile?.name || 'None'}</span>
                </div>
                <div className='flex justify-between'>
                  <span>Uploaded</span>
                  <span className={presignResult ? 'text-emerald-300' : 'text-gray-500'}>
                    {presignResult ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving || !presignResult}
              className='w-full py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60'
            >
              {saving ? 'Saving...' : 'Save Filter'}
            </button>

            {!presignResult && (
              <p className='text-xs text-gray-500 text-center'>Complete Step 1 first.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateLiveFilter;
